import { useEffect, useRef, useState } from 'react';
import { IconButton } from './IconButton.js';

const MAX_SECONDS = 120;

interface VoiceRecorderButtonProps {
  label: string;
  stopLabel: string;
  disabled?: boolean;
  onRecorded: (file: File) => void;
  onError: () => void;
}

export function VoiceRecorderButton({
  label,
  stopLabel,
  disabled,
  onRecorded,
  onError,
}: VoiceRecorderButtonProps) {
  const [seconds, setSeconds] = useState<number | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);

  const cleanup = () => {
    if (timerRef.current !== null) window.clearInterval(timerRef.current);
    timerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setSeconds(null);
  };

  useEffect(
    () => () => {
      if (recorderRef.current?.state === 'recording') {
        recorderRef.current.onstop = null;
        recorderRef.current.stop();
      }
      cleanup();
    },
    [],
  );

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      onError();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size) chunks.push(event.data);
      };
      recorder.onstop = () => {
        const type = recorder.mimeType || 'audio/webm';
        const extension = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm';
        cleanup();
        if (chunks.length)
          onRecorded(new File(chunks, `voice.${extension}`, { type: type.split(';')[0] }));
      };
      recorderRef.current = recorder;
      recorder.start();
      setSeconds(0);
      timerRef.current = window.setInterval(() => {
        setSeconds((current) => {
          const next = (current ?? 0) + 1;
          if (next >= MAX_SECONDS && recorder.state === 'recording') recorder.stop();
          return next;
        });
      }, 1000);
    } catch {
      cleanup();
      onError();
    }
  };

  const recording = seconds !== null;
  return (
    <>
      {recording && (
        <span className="direct-chat-recording" role="timer">
          ● {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}
        </span>
      )}
      <IconButton
        icon={recording ? 'send' : 'mic'}
        label={recording ? stopLabel : label}
        active={recording}
        disabled={disabled}
        onClick={() => {
          if (recording) recorderRef.current?.stop();
          else void start();
        }}
      />
    </>
  );
}
